// Маршруты кабинета.
//
// Оболочку разворачивает каждый экран сам, а не общий обёртчик: состояние при
// этом не теряется — React видит один и тот же тип компонента на том же месте
// дерева и переиспользует его.
//
// Экранов работы теперь ОДИН. Уточнение, подтверждение, выполнение и
// результат были четырьмя адресами, и каждый переход стирал предыдущий:
// хронология одного вопроса собиралась только в голове пользователя. Теперь
// это лента чата, а адреса остались у аккаунта — там разделы и правда разные.

import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { ActiveRunProvider } from './active-run.tsx'
import { SessionProvider, useSession } from './session.tsx'
import { WorkspaceProvider } from './workspace.tsx'
import { ArtifactScreen } from './screens/Artifact.tsx'
import { Chat } from './screens/Chat.tsx'
import { Gallery } from './screens/Gallery.tsx'
import { History } from './screens/History.tsx'
import { Limits } from './screens/Limits.tsx'
import { Preview } from './screens/preview.tsx'
import { Profile } from './screens/Profile.tsx'
import { Tariff } from './screens/Tariff.tsx'
import { Placeholder } from './screens/Placeholder.tsx'
import { Legal } from './screens/Legal.tsx'
import { SignIn } from './screens/SignIn.tsx'
import { Shell } from './shell/Shell.tsx'

/** Экран-заглушка внутри оболочки: адрес живой, содержимое ещё нет. */
const soon = (title: string, issue: string, note?: string) => (
  <Shell>
    <Placeholder title={title} issue={issue} {...(note != null ? { note } : {})} />
  </Shell>
)

function Cabinet() {
  return (
    <Routes>
      {/* Три входа в один экран: пустой чат, чат с заранее выбранной задачей
          и открытый разговор. Развилку решает сам экран — он один и тот же. */}
      <Route path="/" element={<Chat />} />
      <Route path="/task/:slug" element={<Chat />} />
      <Route path="/chat/:id" element={<Chat />} />
      {/* Сохранённый результат по прямой ссылке. Обычный путь к нему — чат, в
          котором он и лежит; этот адрес нужен результату, у которого чата не
          осталось, и старым закладкам. */}
      <Route path="/result/:id" element={<ArtifactScreen />} />
      {/* Четыре раздела аккаунта. Левая колонка на них становится подменю —
          так в макете W07–W10: человек пришёл разбираться с деньгами, и
          предлагать ему тут же начать задачу незачем. */}
      <Route path="/history" element={<History />} />
      <Route path="/tariff" element={<Tariff />} />
      <Route path="/limits" element={<Limits />} />
      <Route path="/profile" element={<Profile />} />
      {/* Витрина компонентов и предпросмотр карточек результата — инструменты
          разработки, в прод их маршруты не попадают: Vite вырезает ветку по
          константе на сборке вместе с кодом витрины и данными макета. */}
      {import.meta.env.DEV ? (
        <>
          <Route
            path="/ui"
            element={
              <Shell>
                <Gallery />
              </Shell>
            }
          />
          <Route path="/ui/:screen" element={<Preview />} />
        </>
      ) : null}
      <Route path="*" element={soon('Такой страницы нет', '', 'Проверьте адрес или вернитесь к задачам.')} />
    </Routes>
  )
}

function Gate() {
  const { me, signedIn, guest } = useSession()

  // null — ещё не спросили сервер. Показывать вход в этот момент нельзя:
  // вошедший увидел бы форму и решил, что его выкинуло.
  if (signedIn == null) return <div style={{ display: 'grid', placeItems: 'center', height: '100%' }} />

  return (
    <Routes>
      {/* ⚠️ Вошедшего сюда пускаем в одном случае — когда ему надо привязать
          почту (SMY-181). Он пришёл по номеру, номер уходит, и путь для него
          нужен между «почта работает» и «номер снят»; безусловный редирект
          отсюда означал бы, что привязать её ему негде. Условие уйдёт вместе
          с входом по номеру (SMY-182). */}
      {/* ⚠️ ПУБЛИЧНЫЕ ДОКУМЕНТЫ — ВНЕ ВСЯКОЙ ПРОВЕРКИ ВХОДА. SMY-215
          Политика обработки данных по ст. 18.1 152-ФЗ должна быть доступна
          неограниченному кругу лиц; оферта — тем, кто ещё не стал
          пользователем, то есть по определению не вошедшим. Маршрут стоит
          ВЫШЕ разбора сессии: любая проверка здесь превратила бы публикацию
          в доступ по паролю. */}
      <Route path="/legal/:doc" element={<Legal />} />

      {/* ⚠️ И ГОСТЬ — тоже. SMY-175: с гостевой сессией человек «вошедший» с
          точки зрения токена, но своего аккаунта у него нет, и ссылка «Уже
          есть аккаунт? Войти» обязана вести на форму, а не отбрасывать его
          обратно в работу. Без этого условия единственный способ попасть в
          свой аккаунт — очистить хранилище браузера. */}
      <Route
        path="/login"
        element={signedIn && !guest && me?.needsEmail !== true ? <Navigate to="/" replace /> : <SignIn />}
      />
      <Route path="*" element={signedIn ? <Cabinet /> : <Navigate to="/login" replace />} />
    </Routes>
  )
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <WorkspaceProvider>
          <ActiveRunProvider>
            <Gate />
          </ActiveRunProvider>
        </WorkspaceProvider>
      </SessionProvider>
    </BrowserRouter>
  )
}
